import React, { FC, ReactNode } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';

import styles from './card-title.module.scss';

import { ArrowRight, Button, Headline3 } from 'views/components';

interface ICardTitleProps {
  children: ReactNode;
  to?: string;
  disabled?: boolean;
  className?: string;
  headingClassName?: string;
  actionsClassName?: string;
  showArrow?: boolean;
  bordered?: boolean;
  /** Rendered on the right, in the actions area before the arrow */
  extra?: ReactNode;
  /** Rendered on the left, right after the title text (e.g. an info icon) */
  titleExtra?: ReactNode;
}

const CardTitle: FC<ICardTitleProps> = ({
  children,
  to,
  disabled,
  className,
  headingClassName,
  actionsClassName,
  showArrow = true,
  bordered,
  extra,
  titleExtra,
}) => {
  const intl = useIntl();
  const titleClassNames = classNames(styles.title, className, {
    [styles.bordered]: bordered,
  });
  const headingClassNames = classNames(styles.heading, headingClassName);
  const actionsClassNames = classNames(styles.actions, actionsClassName);
  const arrowIcon = <ArrowRight size="16px" color="currentColor" />;
  const arrowButton =
    to && !disabled ? (
      <Button
        link={to}
        size="small"
        mode="tertiary"
        color="dark"
        icon={arrowIcon}
        aria-label={intl.formatMessage({ id: 'card.action.open-details' })}
      />
    ) : (
      <Button
        type="button"
        size="small"
        mode="tertiary"
        color="dark"
        icon={arrowIcon}
        aria-label={intl.formatMessage({ id: 'card.action.open-details' })}
        disabled={disabled}
      />
    );

  return (
    <div className={titleClassNames}>
      <div className={headingClassNames}>
        <Headline3>{children}</Headline3>
        {titleExtra}
      </div>
      <div className={actionsClassNames}>
        {extra}
        {showArrow && arrowButton}
      </div>
    </div>
  );
};

export { CardTitle };
